// src/features/settings/components/Settings/index.jsx
import React, { useState, useEffect } from 'react';
import { fetchSettings, updateSettings, updatePageFilters, resetPageFilters } from '../../../../api';
import Button         from '../../../../shared/components/ui/Button';
import LoadingSpinner from '../../../../shared/components/ui/LoadingSpinner';
import styles         from './Settings.module.css';

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width; canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
          'image/jpeg', 0.8
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const Settings = () => {
  const [settings,       setSettings]       = useState({ company_name: '', address: '', phone_number: '' });
  const [defaultFilters, setDefaultFilters] = useState({}); // 👈 تخزين الفلاتر هنا
  
  const [logoFile,      setLogoFile]      = useState(null);
  const [bannerFile,    setBannerFile]    = useState(null);
  const [logoPreview,   setLogoPreview]   = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [loading,       setLoading]       = useState(true);
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');

  useEffect(() => {
    fetchSettings()
      .then((res) => { 
        setSettings(res.data); 
        setLogoPreview(res.data.logo ?? ''); 
        setBannerPreview(res.data.banner ?? '');
        setDefaultFilters(res.data.default_filters || {}); // 👈 جلب الفلاتر
      })
      .catch(() => setError('فشل في تحميل الإعدادات.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => setSettings((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const processed = await compressImage(file);
    const preview   = URL.createObjectURL(processed);
    if (e.target.name === 'logo')   { setLogoFile(processed);   setLogoPreview(preview); }
    if (e.target.name === 'banner') { setBannerFile(processed); setBannerPreview(preview); }
  };

  // ── حفظ الإعدادات الأساسية ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(''); setSuccess('');
    const fd = new FormData();
    fd.append('company_name', settings.company_name);
    fd.append('address',      settings.address);
    fd.append('phone_number', settings.phone_number);
    if (logoFile)   fd.append('logo',   logoFile);
    if (bannerFile) fd.append('banner', bannerFile);
    try { 
      await updateSettings(fd); 
      setSuccess('تم حفظ الإعدادات الأساسية بنجاح!'); 
    }
    catch { setError('حدث خطأ أثناء حفظ الإعدادات.'); }
    finally { setSubmitting(false); }
  };

  // ── تغيير الفلاتر محلياً ──
  const handleFilterChange = (page, key, value) => {
    setDefaultFilters(prev => ({
      ...prev,
      [page]: {
        ...(prev[page] || {}),
        [key]: value
      }
    }));
  };

  // ── حفظ فلتر صفحة معينة في الباك إند ──
  const savePageFilter = async (page) => {
    try {
      await updatePageFilters({ page, settings: defaultFilters[page] });
      setSuccess(`تم حفظ الإعدادات الافتراضية لصفحة (${page})`);
      setTimeout(() => setSuccess(''), 3000); // إخفاء رسالة النجاح بعد 3 ثواني
    } catch (err) {
      setError('فشل في حفظ الفلاتر الافتراضية.');
    }
  };

  // ── إعادة تعيين فلاتر صفحة معينة ──
  const resetPageFilter = async (page) => {
    try {
      const res = await resetPageFilters({ page });
      setDefaultFilters(res.data.settings);
      setSuccess(`تمت إعادة التعيين لصفحة (${page})`);
    } catch (err) {
      setError('فشل في إعادة التعيين.');
    }
  };

  if (loading) return <LoadingSpinner message="جاري تحميل الإعدادات..." />;

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>إعدادات النظام</h1>
      
      {/* عرض الرسائل العامة فوق الشبكة */}
      {error   && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <div className={styles.grid}>

        {/* ── 1. المعلومات الأساسية ── */}
        <form onSubmit={handleSubmit} className={styles.card}>
          <legend className={styles.legend}>المعلومات الأساسية</legend>
          {[
            { name: 'company_name', label: 'اسم الشركة',  type: 'text'     },
            { name: 'phone_number', label: 'رقم الهاتف',  type: 'text'     },
          ].map(({ name, label, type }) => (
            <div key={name} className={styles.field}>
              <label>{label}</label>
              <input type={type} name={name} value={settings[name] ?? ''} onChange={handleChange} className={styles.input} />
            </div>
          ))}
          <div className={styles.field}>
            <label>العنوان</label>
            <textarea name="address" value={settings.address ?? ''} onChange={handleChange} rows={3} className={`${styles.input} ${styles.textarea}`} />
          </div>

          <Button variant="primary" size="md" fullWidth loading={submitting} type="submit" style={{marginTop: 'var(--space-4)'}}>
            حفظ المعلومات الأساسية
          </Button>
        </form>

        {/* ── 2. الفلاتر الافتراضية للصفحات ── */}
        <div className={styles.card}>
          <legend className={styles.legend}>الواجهات الافتراضية</legend>
          
          {/* إعدادات صفحة الحجوزات */}
          <div className={styles.field} style={{ background: 'var(--bg-subtle)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-default)' }}>
            <label>الحالة الافتراضية لسجل الحجوزات</label>
            <select 
              value={defaultFilters?.bookings?.status || 'active'} 
              onChange={(e) => handleFilterChange('bookings', 'status', e.target.value)}
              className={styles.input}
            >
              <option value="active">الحجوزات النشطة (افتراضي)</option>
              <option value="all">كل الحالات</option>
              <option value="pending">قيد الانتظار</option>
              <option value="confirmed">مؤكد</option>
              <option value="in_progress">قيد التنفيذ</option>
              <option value="completed">مكتمل</option>
            </select>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <Button variant="info" size="sm" type="button" onClick={() => savePageFilter('bookings')}>حفظ الفلتر</Button>
              <Button variant="secondary" size="sm" type="button" onClick={() => resetPageFilter('bookings')}>إعادة تعيين</Button>
            </div>
          </div>

          {/* إعدادات صفحة المهام */}
          <div className={styles.field} style={{ background: 'var(--bg-subtle)', padding: '15px', borderRadius: '10px', border: '1px solid var(--border-default)' }}>
            <label>الحالة الافتراضية للوحة المهام</label>
            <select 
              value={defaultFilters?.tasks?.status || 'active'} 
              onChange={(e) => handleFilterChange('tasks', 'status', e.target.value)}
              className={styles.input}
            >
              <option value="active">المهام النشطة (افتراضي)</option>
              <option value="all">كل الحالات</option>
              <option value="todo">مطلوبة</option>
              <option value="in_progress">قيد التنفيذ</option>
              <option value="done">مكتملة</option>
            </select>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <Button variant="info" size="sm" type="button" onClick={() => savePageFilter('tasks')}>حفظ الفلتر</Button>
              <Button variant="secondary" size="sm" type="button" onClick={() => resetPageFilter('tasks')}>إعادة تعيين</Button>
            </div>
          </div>

        </div>

        {/* ── 3. الشعار والبانر ── */}
        <form onSubmit={handleSubmit} className={styles.card} style={{ gridColumn: '1 / -1' }}>
          <legend className={styles.legend}>الشعار وبانر الطباعة</legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-4)' }}>
            {[
              { name: 'logo',   label: 'شعار الشركة',  preview: logoPreview   },
              { name: 'banner', label: 'بانر الطباعة', preview: bannerPreview },
            ].map(({ name, label, preview }) => (
              <div key={name} className={styles.uploadBox}>
                <label>{label}</label>
                <input type="file" name={name} onChange={handleFileChange} accept="image/*" />
                {preview && <img src={preview} alt={`معاينة ${label}`} className={styles.preview} />}
              </div>
            ))}
          </div>
          <Button variant="primary" size="md" loading={submitting} type="submit" style={{marginTop: 'var(--space-4)'}}>
            حفظ الصور
          </Button>
        </form>

      </div>
    </div>
  );
};

export default Settings;